import { BriefcaseIcon, DownloadIcon, LoaderCircleIcon, MoreHorizontalIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";

import type { Policy, PolicyJob } from "../../../state/policies";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Menu, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "../../ui/menu";
import { formatTimestamp, timeAgo } from "./documentTime";

/**
 * The document page's header: what this page is, which document it is about,
 * and the actions the server can actually perform.
 *
 * Re-processing and vector-store re-uploads are deliberately absent — the API
 * exposes no such endpoint, and a button that cannot do its job is worse than
 * no button.
 */
export function DocumentHeader({
  policy,
  jobs,
  jobsPending,
  selectedJobId,
  canManage,
  deleting,
  onOpenJobs,
  onSelectJob,
  onDownload,
  onDelete,
  now,
}: {
  policy: Policy;
  jobs: ReadonlyArray<PolicyJob>;
  jobsPending: boolean;
  selectedJobId: string | null;
  canManage: boolean;
  deleting: boolean;
  onOpenJobs: () => void;
  onSelectJob: (jobId: string | null) => void;
  onDownload: () => void;
  onDelete: () => void;
  now: number;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">
          Processing details
        </h1>
        <p className="mt-0.5 truncate text-sm text-muted-foreground">{policy.filename}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Menu
          onOpenChange={(open) => {
            if (open) onOpenJobs();
          }}
        >
          <MenuTrigger render={<Button type="button" size="sm" variant="outline" />}>
            <BriefcaseIcon className="size-3.5" />
            Runs
            {jobs.length > 0 ? (
              <span className="ms-1 rounded-full bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
                {jobs.length}
              </span>
            ) : null}
          </MenuTrigger>
          <MenuPopup align="end" className="w-[320px]">
            {jobsPending && jobs.length === 0 ? (
              <div className="flex items-center justify-center p-6">
                <LoaderCircleIcon className="size-4 animate-spin text-muted-foreground" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                This document has never been processed.
              </div>
            ) : (
              jobs.map((job, index) => {
                // The newest run is what the page shows by default, so it is
                // "active" both when explicitly selected and when nothing is.
                const active =
                  selectedJobId === job.id || (selectedJobId === null && index === 0);
                return (
                  <MenuItem
                    key={job.id}
                    onClick={() => onSelectJob(index === 0 ? null : job.id)}
                    className="flex-col items-start gap-1"
                  >
                    <span className="flex w-full items-center gap-2">
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {job.id.slice(0, 8)}
                      </span>
                      <Badge
                        variant={
                          job.status === "completed"
                            ? "success"
                            : job.status === "failed"
                              ? "error"
                              : "secondary"
                        }
                        className="rounded-full px-2 py-0 text-[10px] font-normal"
                      >
                        {job.status}
                      </Badge>
                      {active ? (
                        <Badge
                          variant="outline"
                          className="ms-auto rounded-full px-2 py-0 text-[10px] font-normal"
                        >
                          Showing
                        </Badge>
                      ) : null}
                    </span>
                    <span
                      className="text-[11px] text-muted-foreground"
                      title={formatTimestamp(job.createdAt)}
                    >
                      {timeAgo(job.createdAt, now)}
                    </span>
                  </MenuItem>
                );
              })
            )}
          </MenuPopup>
        </Menu>

        <Menu>
          <MenuTrigger
            render={
              <Button type="button" size="icon-sm" variant="ghost" aria-label="Document actions" />
            }
          >
            <MoreHorizontalIcon className="size-4" />
          </MenuTrigger>
          <MenuPopup align="end">
            <MenuItem onClick={onDownload}>
              <DownloadIcon className="size-4" />
              Download PDF
            </MenuItem>
            {canManage ? (
              <>
                <MenuSeparator />
                <MenuItem variant="destructive" onClick={() => setConfirmDelete(true)}>
                  <Trash2Icon className="size-4" />
                  Delete document
                </MenuItem>
              </>
            ) : null}
          </MenuPopup>
        </Menu>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes{" "}
              <span className="font-medium text-foreground">{policy.filename}</span>, every
              processing run recorded for it and the chunks eve grounds its answers in. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>Cancel</AlertDialogClose>
            <Button
              variant="destructive"
              disabled={deleting}
              onClick={() => {
                setConfirmDelete(false);
                onDelete();
              }}
            >
              {deleting ? <LoaderCircleIcon className="size-3.5 animate-spin" /> : null}
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </div>
  );
}
